// ============================================
// FILE: src/services/SecureAPI.js
// COMPLETE VERSION WITH STREAMING SUPPORT
// CLIENT-SIDE API WRAPPER - NO KEYS EXPOSED
// ============================================

import { auth } from '../../firebase/firebase';

const FUNCTIONS_BASE_URL = 'https://us-central1-fir-7c9c9.cloudfunctions.net';

class SecureAPIClient {
  constructor() {
    this.baseURL = FUNCTIONS_BASE_URL;
    this.speechToken = null;
    this.tokenExpiry = null;
  }

  // ============================================
  // GET AUTH TOKEN
  // ============================================
  async getAuthToken() {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('Not authenticated - please log in');
    }
    return await user.getIdToken();
  }

  // ============================================
  // AZURE OPENAI - NON-STREAMING (Default)
  // ============================================
  async callAzureOpenAI(messages, options = {}) {
    try {
      const token = await this.getAuthToken();
      
      // Build request body
      const requestBody = {
        messages,
        temperature: options.temperature || 0.85,
        max_tokens: options.max_tokens || 1500
      };
      
      // Only add tools if they exist and have items
      if (options.tools && Array.isArray(options.tools) && options.tools.length > 0) {
        requestBody.tools = options.tools;
        if (options.tool_choice) {
          requestBody.tool_choice = options.tool_choice;
        }
      }
      
      // Add response_format if specified
      if (options.response_format) {
        requestBody.response_format = options.response_format;
      }
      
      const response = await fetch(`${this.baseURL}/azureOpenAI`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || `Request failed: ${response.status}`);
      }

      return await response.json();
      
    } catch (error) {
      console.error('Azure OpenAI Error:', error);
      throw error;
    }
  }

  // ============================================
  // ✨ NEW: AZURE OPENAI - STREAMING VERSION
  // Returns a ReadableStream reader for true streaming
  // ============================================
  async callAzureOpenAIStreaming(messages, options = {}) {
    try {
      const token = await this.getAuthToken();
      
      // Build request body with stream: true
      const requestBody = {
        messages,
        stream: true,  // ✅ Enable streaming
        temperature: options.temperature || 0.85,
        max_tokens: options.max_tokens || 1500
      };
      
      // Only add tools if they exist and have items
      if (options.tools && Array.isArray(options.tools) && options.tools.length > 0) {
        requestBody.tools = options.tools;
        if (options.tool_choice) {
          requestBody.tool_choice = options.tool_choice;
        }
      }
      
      // Add response_format if specified
      if (options.response_format) {
        requestBody.response_format = options.response_format;
      }
      
      console.log('📤 Calling streaming endpoint...');
      
      const response = await fetch(`${this.baseURL}/azureOpenAI`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || `Streaming request failed: ${response.status}`);
      }

      // ✅ Return the stream reader directly
      console.log('✅ Stream established, returning reader');
      return response.body.getReader();
      
    } catch (error) {
      console.error('❌ Azure OpenAI Streaming Error:', error);
      throw error;
    }
  }

  // ============================================
  // HUME TTS (Drop-in replacement)
  // ============================================
  async callHumeTTS(text, voiceId) {
    const token = await this.getAuthToken();
    
    const response = await fetch(`${this.baseURL}/humeTTS`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        text,
        voice: voiceId  // Just send the ID, not an object
      })
    });

    if (!response.ok) {
      throw new Error(`TTS failed: ${response.status}`);
    }

    return await response.blob();
  }

  // ============================================
  // ✨ AZURE SPEECH TOKEN
  // Returns a temporary token valid for 10 minutes
  // Caches token for 9 minutes to avoid unnecessary requests
  // ============================================
  async getAzureSpeechToken() {
    const now = Date.now();
    
    // Return cached token if still valid (9 min cache, token valid 10 min)
    if (this.speechToken && this.tokenExpiry && now < this.tokenExpiry) {
      console.log('✅ Using cached speech token (expires in', Math.round((this.tokenExpiry - now) / 1000), 'seconds)');
      return this.speechToken;
    }

    try {
      const token = await this.getAuthToken();
      
      console.log('🔐 Requesting new Azure Speech token...');
      
      const response = await fetch(`${this.baseURL}/getAzureSpeechToken`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || `Token request failed: ${response.status}`);
      }

      const data = await response.json();
      
      // Cache token for 9 minutes (540000 ms)
      // Token is valid for 10 minutes, we refresh at 9 to be safe
      this.speechToken = data;
      this.tokenExpiry = now + 540000;
      
      console.log('✅ New speech token obtained (valid for 10 minutes)');
      return data;
      
    } catch (error) {
      console.error('❌ Speech Token Error:', error);
      
      // Clear cache on error
      this.speechToken = null;
      this.tokenExpiry = null;
      
      throw error;
    }
  }
// ============================================
// ✨ HUME TTS SECURE (Server-side key)
// ============================================
async callHumeTTSSecure(text, voiceId, speed = 1.05) {
  try {
    const token = await this.getAuthToken();
    
    const response = await fetch(`${this.baseURL}/humeTTSSecure`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        text,
        voiceId,  // Send just the ID string
        speed
      })
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || `TTS failed: ${response.status}`);
    }

    return await response.blob();
    
  } catch (error) {
    console.error('Hume TTS Secure Error:', error);
    throw error;
  }
}
  // ============================================
  // ✨ REFRESH SPEECH TOKEN
  // Call this manually if you need to force a refresh
  // ============================================
  async refreshSpeechToken() {
    console.log('🔄 Force refreshing speech token...');
    this.speechToken = null;
    this.tokenExpiry = null;
    return await this.getAzureSpeechToken();
  }

  // ============================================
  // ✨ CHECK IF TOKEN IS VALID
  // Returns true if cached token is still valid
  // ============================================
  isSpeechTokenValid() {
    if (!this.speechToken || !this.tokenExpiry) {
      return false;
    }
    return Date.now() < this.tokenExpiry;
  }

  // ============================================
  // UNIVERSAL SEARCH (Drop-in replacement)
  // ============================================
  async callUniversalSearch(query, maxResults = 5) {
    try {
      const token = await this.getAuthToken();
      
      const response = await fetch(
        `${this.baseURL}/universalSearch?query=${encodeURIComponent(query)}&maxResults=${maxResults}`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || `Search failed: ${response.status}`);
      }

      return await response.json();
      
    } catch (error) {
      console.error('Universal Search Error:', error);
      throw error;
    }
  }
// ============================================
  // ✨ PENTEST — generic Cloud Function caller
  // Used by SharkBase for runAutoScan + runPentestLayer
  // ============================================
  async callFunction(name, body = {}, method = 'POST') {
    try {
      const token = await this.getAuthToken();

      const isGet = method === 'GET';
      const url = isGet
        ? `${this.baseURL}/${name}?${new URLSearchParams(body).toString()}`
        : `${this.baseURL}/${name}`;

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          ...(isGet ? {} : { 'Content-Type': 'application/json' }),
        },
        ...(isGet ? {} : { body: JSON.stringify(body) }),
      });

      if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}));
        // ✅ Attach the real HTTP status code to the error object
        // so callers like the polling loop can detect 404 reliably
        const err = new Error(errorBody.error || `${name} failed: ${response.status}`);
        err.status = response.status;
        throw err;
      }

      return await response.json();

    } catch (error) {
      // Re-attach status from message string if somehow lost during re-throw
      if (error.status == null) {
        const match = error.message?.match(/:?\s*(\d{3})$/);
        if (match) error.status = parseInt(match[1], 10);
      }
      console.error(`callFunction(${name}) error:`, error);
      throw error;
    }
  }
}


// ============================================
// EXPORT SINGLETON
// ============================================
export const SecureAPI = new SecureAPIClient();